import { useState, useEffect, useCallback, useRef } from 'react';
import { CiLock } from 'react-icons/ci';
import { IoKeyOutline } from 'react-icons/io5';
import { BiEdit, BiSend } from 'react-icons/bi';
import { RiFileListLine } from 'react-icons/ri';
import { FiSearch } from 'react-icons/fi';
import { MdClose, MdLockOutline, MdOutlineDashboard } from 'react-icons/md';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import ActionButton from '../components/ui/ActionButton';
import PopupWrapper from '../components/utils/PopupWrapper';
import InputField from '../components/ui/InputField';
import { SiEndeavouros } from 'react-icons/si';
import { MdOutlineSecurity } from 'react-icons/md';
import Toggle from '../components/ui/Toggle';
import { FiUsers } from 'react-icons/fi';
import { GoProject } from 'react-icons/go';
import { RiTeamLine } from 'react-icons/ri';
import {
  MdAssignmentAdd,
  MdPendingActions,
  MdRateReview,
  MdLocalShipping,
  MdRequestQuote,
  MdPauseCircle,
  MdCancel,
  MdPayments,
  MdRefresh,
} from 'react-icons/md';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  fetchPermissions,
  createPermission,
  updatePermission,
  fetchPermissionById,
  searchPermissions,
} from '../redux/actions/permissionActions';
import {
  clearPermissionError,
  clearCurrentPermission,
  clearSearchResults,
} from '../redux/slices/permissionSlice';
import LoadingAnimation from '../components/utils/Loading';
import toast from 'react-hot-toast';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { withToast } from '../utils/toastUtils';
import { PermissionData } from '../redux/types/permissionTypes';
import { ReactElement, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';

interface EntityPermission {
  read: boolean;
  create: boolean;
  edit: boolean;
  manage?: boolean; // Optional fourth permission for specific entities like Employee
}

interface PermissionState {
  [key: string]: EntityPermission;
}

interface WorkflowPermission {
  enabled: boolean;
}

interface WorkflowState {
  [key: string]: WorkflowPermission;
}

// Extended ActionButton props to include disabled
interface ExtendedActionButtonProps {
  onClick: () => void;
  icon: ReactElement;
  variant: string;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  mobileFixed?: boolean;
  children: React.ReactNode;
}

// Extended Toggle props to include disabled
interface ExtendedToggleProps {
  enabled: boolean;
  onChange: () => void;
  size: string;
  disabled?: boolean;
}

// Type for designation object
interface DesignationItem {
  id: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
  permissions: PermissionData[];
}

const DesignationPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentPermission, loading, pagination, searchResults, searching } = useAppSelector(
    state => state.permissions
  );
  // Get user data and permissions from auth state
  const { user } = useAppSelector(state => state.auth);

  // Extract role permissions from user data
  const [userPermissions, setUserPermissions] = useState({
    roleRead: false,
    roleWrite: false,
    roleEdit: false,
  });

  // Initialize our permission state from user data
  useEffect(() => {
    // Only proceed with state updates if user exists
    if (user) {
      console.log('User data for permissions check:', user);

      // Check permissions directly instead of relying on state updates
      let hasReadPermission = false;
      let hasWritePermission = false;
      let hasEditPermission = false;

      // Super Admin has all permissions
      if (user.designation === 'superAdmin') {
        hasReadPermission = true;
        hasWritePermission = true;
        hasEditPermission = true;
        // Set permission state for other components
        setUserPermissions({
          roleRead: true,
          roleWrite: true,
          roleEdit: true,
        });
      } else if (user.permissions) {
        // For debugging
        console.log('User permissions object:', user.permissions);

        // Handle different permissions object formats and determine permissions directly
        let roleReadValue = false;
        let roleWriteValue = false;
        let roleEditValue = false;

        // Check if permissions is an object with numeric keys (like {0: {...}})
        if (typeof user.permissions === 'object' && '0' in user.permissions) {
          console.log('Found permissions in format {0: {...}}');
          //@ts-ignore
          roleReadValue = Boolean(user.permissions[0].roleRead);
          //@ts-ignore
          roleWriteValue = Boolean(user.permissions[0].roleWrite);
          //@ts-ignore
          roleEditValue = Boolean(user.permissions[0].roleEdit);
        }
        // Check if permissions is an array
        else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
          console.log('Found permissions in array format');
          roleReadValue = Boolean(user.permissions[0].roleRead);
          roleWriteValue = Boolean(user.permissions[0].roleWrite);
          roleEditValue = Boolean(user.permissions[0].roleEdit);
        }
        // Direct object access if neither of the above
        else if (typeof user.permissions === 'object') {
          console.log('Found permissions in direct object format');
          roleReadValue = Boolean(user.permissions.roleRead);
          roleWriteValue = Boolean(user.permissions.roleWrite);
          roleEditValue = Boolean(user.permissions.roleEdit);
        }

        console.log('Setting permissions to:', {
          roleRead: roleReadValue,
          roleWrite: roleWriteValue,
          roleEdit: roleEditValue,
        });

        // Check permissions directly
        hasReadPermission = roleReadValue;
        hasWritePermission = roleWriteValue;
        hasEditPermission = roleEditValue;

        // Set the state with the determined values
        setUserPermissions({
          roleRead: roleReadValue,
          roleWrite: roleWriteValue,
          roleEdit: roleEditValue,
        });
      }

      // Do the redirect check immediately based on the direct permission check
      console.log('Direct permission check result:', hasReadPermission);
      if (!hasReadPermission) {
        toast.error("You don't have permission to access this page", {
          duration: 3000,
        });
        navigate('/dashboard');
      }
    } else if (user === null) {
      // If user is null (not loading), redirect to login
      toast.error('Please log in to access this page', {
        duration: 3000,
      });
      navigate('/auth/login');
    }
  }, [user, navigate]);

  const [showModal, setShowModal] = useState(false);
  const [designationTitle, setDesignationTitle] = useState('');
  const [editingDesignation, setEditingDesignation] = useState<DesignationItem | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [localLoading, setLocalLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [viewOnlyMode, setViewOnlyMode] = useState(false);
  const [enableAllPermissions, setEnableAllPermissions] = useState(false);

  const [visibleDesignations, setVisibleDesignations] = useState<DesignationItem[]>([]);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [page, setPage] = useState(1);

  // Search related states
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100 &&
      !isFetchingMore &&
      pagination &&
      pagination.currentPage < pagination.pages
    ) {
      setIsFetchingMore(true);
      setPage(prevPage => prevPage + 1);
    }
  }, [isFetchingMore, pagination]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Initial data fetch and scroll handler
  useEffect(() => {
    // Only fetch if user has read permission
    if (userPermissions.roleRead || user?.designation === 'superAdmin') {
      setLocalLoading(true);
      dispatch(fetchPermissions({ page: 1, limit: 10 }))
        .unwrap()
        .then(response => {
          if (response?.data) {
            setVisibleDesignations(response.data);
          }
        })
        .catch(error => {
          console.error('Error fetching designations:', error);
        })
        .finally(() => {
          setLocalLoading(false);
        });
    }
  }, [dispatch, userPermissions.roleRead, user]);

  // Fetch more data when page changes - only this one useEffect should handle it
  useEffect(() => {
    if (page > 1 && (userPermissions.roleRead || user?.designation === 'superAdmin')) {
      setIsFetchingMore(true);
      dispatch(fetchPermissions({ page, limit: 10 }))
        .unwrap()
        .then(response => {
          if (response?.data) {
            // Make sure we're not adding duplicate data
            const newDesignations = response.data;
            const existingIds = new Set(visibleDesignations.map(p => p.id));

            // Only add items that don't exist in the current list
            const uniqueNewDesignations = newDesignations.filter(
              (newDesig: DesignationItem) => !existingIds.has(newDesig.id)
            );

            if (uniqueNewDesignations.length > 0) {
              setVisibleDesignations(prev => [...prev, ...uniqueNewDesignations]);
            }
          }
        })
        .catch(error => {
          console.error('Error fetching more designations:', error);
          // Reset page on error to allow retry
          setPage(prev => prev - 1);
        })
        .finally(() => {
          setIsFetchingMore(false);
        });
    }
  }, [page, dispatch, visibleDesignations, userPermissions.roleRead, user]);

  // Define state for entity permissions
  const [entityPermissions, setEntityPermissions] = useState<PermissionState>({
    Customer: { read: false, create: false, edit: false },
    Employee: { read: false, create: false, edit: false, manage: false },
    Project: { read: false, create: false, edit: false },
    Role: { read: false, create: false, edit: false },
  });

  // Define state for workflow permissions
  const [workflowPermissions, setWorkflowPermissions] = useState<WorkflowState>({
    'Can Assign Project': { enabled: false },
    'Can Mark In Progress': { enabled: false },
    'Can Review': { enabled: false },
    'Can Deliver Project': { enabled: false },
    'Can Bill Project': { enabled: false },
    'Can Hold Project': { enabled: false },
    'Can Cancel Project': { enabled: false },
    'Can Receive Payment': { enabled: false },
    'Can Reopen Project': { enabled: false },
  });

  // Define state for dashboard permissions
  const [dashboardPermission, setDashboardPermission] = useState<WorkflowState>({
    'Finance Access': { enabled: false },
    'Application Access': { enabled: false },
  });

  // Define state for project-specific permissions
  const [projectSpecificPermissions, setProjectSpecificPermissions] = useState<WorkflowState>({
    'Can Change Project Name': { enabled: false },
    'Can Change Article Number': { enabled: false },
    'Can Change Company Name': { enabled: false },
    'Can Transfer Project': { enabled: false },
    'Can Change Project Description': { enabled: false },
    'Can Change Project Remarks': { enabled: false }, // UI shows "Select Service" but backend remains "Project Remarks"
    'Can Change Project Cost': { enabled: false },
    'Can Upload Product Image': { enabled: false },
  });

  const entityIcons: Record<string, ReactElement> = {
    Dashboard: <MdOutlineDashboard className="text-gray-600" size={18} />,
    Customer: <FiUsers className="text-gray-600" size={18} />,
    Project: <GoProject className="text-gray-600" size={18} />,
    Employee: <RiTeamLine className="text-gray-600" size={18} />,
    Role: <MdOutlineSecurity className="text-gray-600" size={18} />,
  };

  const workflowIcons: Record<string, ReactElement> = {
    'Assign Project': <MdAssignmentAdd className="text-gray-600" size={18} />,
    'In Progress': <MdPendingActions className="text-gray-600" size={18} />,
    Review: <MdRateReview className="text-gray-600" size={18} />,
    'Delivered Project': <MdLocalShipping className="text-gray-600" size={18} />,
    'Bill Project': <MdRequestQuote className="text-gray-600" size={18} />,
    'Hold Project': <MdPauseCircle className="text-gray-600" size={18} />,
    'Cancel Project': <MdCancel className="text-gray-600" size={18} />,
    'Receive Payment': <MdPayments className="text-gray-600" size={18} />,
    'Reopen Project': <MdRefresh className="text-gray-600" size={18} />,
  };

  // Define Dashboard icon for the dashboard permission
  const dashboardIcons: Record<string, ReactElement> = {
    'Finance Access': <MdPayments className="text-gray-600" size={18} />,
    'Application Access': <MdOutlineDashboard className="text-gray-600" size={18} />,
  };

  // Define icons for project-specific permissions
  const projectSpecificIcons: Record<string, ReactElement> = {
    'Change Project Name': <BiEdit className="text-gray-600" size={18} />,
    'Change Article Number': <RiFileListLine className="text-gray-600" size={18} />,
    'Change Company Name': <FiUsers className="text-gray-600" size={18} />,
    'Transfer Project': <BiSend className="text-gray-600" size={18} />,
    'Change Project Description': <RiFileListLine className="text-gray-600" size={18} />,
    'Change Project Remarks': <RiFileListLine className="text-gray-600" size={18} />, // UI shows "Select Service" but backend remains "Project Remarks"
    'Change Project Cost': <MdPayments className="text-gray-600" size={18} />,
    'Upload Product Image': <GoProject className="text-gray-600" size={18} />,
  };

  // Function to handle toggling dashboard permission
  const handleDashboardToggle = (permission: string) => {
    console.log(
      `Toggling dashboard permission ${permission} from`,
      dashboardPermission[permission].enabled,
      'to',
      !dashboardPermission[permission].enabled
    );

    // Check if we're turning a permission off
    const isDisabling = dashboardPermission[permission].enabled;

    // Use functional update to ensure we're working with the latest state
    setDashboardPermission(prevState => {
      const newState = {
        ...prevState,
        [permission]: {
          enabled: !prevState[permission].enabled, // Toggle based on current state
        },
      };

      console.log(`[DEBUG] Updated ${permission} permission:`, newState[permission]);

      return newState;
    });

    // If we're disabling a permission, immediately turn off the "Enable All" toggle
    if (isDisabling) {
      setEnableAllPermissions(false);
    }
    // If we're enabling a permission, check if this would make all permissions enabled
    else {
      // We need to check all permissions in the next tick when state has been updated
      setTimeout(() => {
        setEnableAllPermissions(checkIfAllPermissionsEnabled());
      }, 0);
    }
  };

  // Update form when currentPermission changes (especially when editing)
  useEffect(() => {
    if (currentPermission && editingDesignation) {
      // Set designation title
      setDesignationTitle(currentPermission.name);

      // Handle the permission data structure - permission data is an array
      const permissionsArray = Array.isArray(currentPermission.permissions)
        ? currentPermission.permissions
        : [currentPermission.permissions];

      // Get the first permission object from the array if it exists
      if (permissionsArray && permissionsArray.length > 0) {
        const permData = permissionsArray[0];

        // Helper function to force boolean conversion for any value
        const forceBool = (value: any): boolean => value == true;

        // Set entity permissions with properly forced boolean values
        const newEntityPermissions = {
          Customer: {
            read: forceBool(permData.customerRead),
            create: forceBool(permData.customerWrite),
            edit: forceBool(permData.customerEdit),
          },
          Employee: {
            read: forceBool(permData.employeeRead),
            create: forceBool(permData.employeeWrite),
            edit: forceBool(permData.employeeEdit),
            manage: forceBool(permData.canManageEmployee),
          },
          Project: {
            read: forceBool(permData.projectRead),
            create: forceBool(permData.projectWrite),
            edit: forceBool(permData.projectEdit),
          },
          Role: {
            read: forceBool(permData.roleRead),
            create: forceBool(permData.roleWrite),
            edit: forceBool(permData.roleEdit),
          },
        };
        setEntityPermissions(newEntityPermissions);

        // Set workflow permissions with properly forced boolean values
        const newWorkflowPermissions = {
          'Can Assign Project': { enabled: forceBool(permData.canAssignProject) },
          'Can Mark In Progress': { enabled: forceBool(permData.canInProgress) },
          'Can Review': { enabled: forceBool(permData.canReview) },
          'Can Deliver Project': { enabled: forceBool(permData.canDeliverProject) },
          'Can Bill Project': { enabled: forceBool(permData.canBillProject) },
          'Can Hold Project': { enabled: forceBool(permData.canHoldProject) },
          'Can Cancel Project': { enabled: forceBool(permData.canCancelProject) },
          'Can Receive Payment': { enabled: forceBool(permData.canReceivePayment) },
          'Can Reopen Project': { enabled: forceBool(permData.canReopenProject) },
        };
        setWorkflowPermissions(newWorkflowPermissions);

        // Set dashboard permissions for Finance Access and Application Access
        const newDashboardPermission = {
          'Finance Access': { enabled: forceBool(permData.financeAccess) },
          'Application Access': { enabled: forceBool(permData.applicationAccess) },
        };
        setDashboardPermission(newDashboardPermission);

        // Set project-specific permissions with properly forced boolean values
        const newProjectSpecificPermissions = {
          'Can Change Project Name': { enabled: forceBool(permData.canChangeProjectName) },
          'Can Change Article Number': { enabled: forceBool(permData.canChangeArticleNumber) },
          'Can Change Company Name': { enabled: forceBool(permData.canChangeCompanyName) },
          'Can Transfer Project': { enabled: forceBool(permData.canTransferProject) },
          'Can Change Project Description': { enabled: forceBool(permData.canChangeProjectDescription) },
          'Can Change Project Remarks': { enabled: forceBool(permData.canChangeProjectRemarks) },
          'Can Change Project Cost': { enabled: forceBool(permData.canChangeProjectCost) },
          'Can Upload Product Image': { enabled: forceBool(permData.canUploadProductImage) },
        };
        setProjectSpecificPermissions(newProjectSpecificPermissions);

        // Check if all permissions are enabled and update the master toggle accordingly
        setTimeout(() => {
          setEnableAllPermissions(checkIfAllPermissionsEnabled());
        }, 0);
      }

      // Clear local loading state after data is set
      setLocalLoading(false);
    }
  }, [currentPermission, editingDesignation]);

  const resetFormState = () => {
    // Reset entity permissions
    setEntityPermissions({
      Customer: { read: false, create: false, edit: false },
      Employee: { read: false, create: false, edit: false, manage: false },
      Project: { read: false, create: false, edit: false },
      Role: { read: false, create: false, edit: false },
    });

    // Reset dashboard permissions
    setDashboardPermission({
      'Finance Access': { enabled: false },
      'Application Access': { enabled: false },
    });

    // Reset workflow permissions
    setWorkflowPermissions({
      'Can Assign Project': { enabled: false },
      'Can Mark In Progress': { enabled: false },
      'Can Review': { enabled: false },
      'Can Deliver Project': { enabled: false },
      'Can Bill Project': { enabled: false },
      'Can Hold Project': { enabled: false },
      'Can Cancel Project': { enabled: false },
      'Can Receive Payment': { enabled: false },
      'Can Reopen Project': { enabled: false },
    });

    // Reset project-specific permissions
    setProjectSpecificPermissions({
      'Can Change Project Name': { enabled: false },
      'Can Change Article Number': { enabled: false },
      'Can Change Company Name': { enabled: false },
      'Can Transfer Project': { enabled: false },
      'Can Change Project Description': { enabled: false },
      'Can Change Project Remarks': { enabled: false }, // UI shows "Select Service" but backend remains "Project Remarks"
      'Can Change Project Cost': { enabled: false },
      'Can Upload Product Image': { enabled: false },
    });
  };

  const handleCreateDesignation = () => {
    // Check create permission
    if (!userPermissions.roleWrite && user?.designation !== 'superAdmin') {
      toast.error("You don't have permission to create designations", {
        duration: 3000,
      });
      return;
    }

    setEditingDesignation(null);
    setDesignationTitle('');
    dispatch(clearCurrentPermission());
    resetFormState();
    setCurrentStep(1);
    setShowModal(true);
  };

  const handleEditDesignation = async (designation: DesignationItem) => {
    // Check edit permission
    if (!userPermissions.roleEdit && user?.designation !== 'superAdmin') {
      toast.error("You don't have permission to edit designations", {
        duration: 3000,
      });
      return;
    }

    // Set local loading state
    setLocalLoading(true);

    // Set editing state first
    setEditingDesignation(designation);
    setCurrentStep(2);
    setShowModal(true);

    try {
      // Fetch the designation data directly without any toast
      await dispatch(fetchPermissionById(designation.id)).unwrap();
    } catch (error) {
      console.error('Error fetching designation details:', error);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleSaveDesignation = async () => {
    // For step 1, we only check write permission
    if (currentStep === 1) {
      if (!userPermissions.roleWrite && user?.designation !== 'superAdmin') {
        toast.error("You don't have permission to create designations", {
          duration: 3000,
        });
        return;
      }

      // Trim the designation title to avoid extra spaces
      const trimmedTitle = designationTitle.trim();

      if (!trimmedTitle) {
        // Keep only this error toast since it's essential for user experience
        toast.error('Please enter a designation name');
        return;
      }

      // Update the state with trimmed value to ensure it's used throughout the component
      setDesignationTitle(trimmedTitle);

      setCurrentStep(2);
    } else {
      // For step 2, check write permission for new designations or edit permission for existing ones
      if (editingDesignation && !userPermissions.roleEdit && user?.designation !== 'superAdmin') {
        toast.error("You don't have permission to edit designations", {
          duration: 3000,
        });
        return;
      } else if (
        !editingDesignation &&
        !userPermissions.roleWrite &&
        user?.designation !== 'superAdmin'
      ) {
        toast.error("You don't have permission to create designations", {
          duration: 3000,
        });
        return;
      }

      try {
        // Set loading state
        setSaveLoading(true);

        // For updating a designation, we need to include the ID of the existing permission record
        let permissionId = '';

        // Get the ID from currentPermission if we're editing
        if (
          editingDesignation &&
          currentPermission &&
          Array.isArray(currentPermission.permissions) &&
          currentPermission.permissions.length > 0
        ) {
          permissionId = currentPermission.permissions[0]?.id;
        }

        // Get the latest state values directly from the state objects
        // This ensures we capture the most recent changes
        const latestEntityPermissions = { ...entityPermissions };
        const latestWorkflowPermissions = { ...workflowPermissions };

        console.log(
          '[DEBUG] Latest Role permissions before API call:',
          latestEntityPermissions.Role
        );

        // Force all boolean values to actual booleans, not strings or numbers
        const designationData = {
          name: designationTitle,
          permissions: {
            // If editing, include the ID
            ...(permissionId ? { id: permissionId } : {}),

            // Dashboard access permissions
            applicationAccess: Boolean(dashboardPermission['Application Access'].enabled),
            financeAccess: Boolean(dashboardPermission['Finance Access'].enabled),

            // Match the exact permission names from the backend with explicit boolean conversions
            customerRead: Boolean(latestEntityPermissions.Customer.read),
            customerWrite: Boolean(latestEntityPermissions.Customer.create),
            customerEdit: Boolean(latestEntityPermissions.Customer.edit),
            employeeRead: Boolean(latestEntityPermissions.Employee.read),
            employeeWrite: Boolean(latestEntityPermissions.Employee.create),
            employeeEdit: Boolean(latestEntityPermissions.Employee.edit),
            canManageEmployee: Boolean(latestEntityPermissions.Employee.manage),
            projectRead: Boolean(latestEntityPermissions.Project.read),
            projectWrite: Boolean(latestEntityPermissions.Project.create),
            projectEdit: Boolean(latestEntityPermissions.Project.edit),

            // Use the captured latest state for Role permissions
            roleRead: Boolean(latestEntityPermissions.Role.read),
            roleWrite: Boolean(latestEntityPermissions.Role.create),
            roleEdit: Boolean(latestEntityPermissions.Role.edit),

            // Workflow permissions
            canAssignProject: Boolean(latestWorkflowPermissions['Can Assign Project'].enabled),
            canInProgress: Boolean(latestWorkflowPermissions['Can Mark In Progress'].enabled),
            canReview: Boolean(latestWorkflowPermissions['Can Review'].enabled),
            canDeliverProject: Boolean(latestWorkflowPermissions['Can Deliver Project'].enabled),
            canBillProject: Boolean(latestWorkflowPermissions['Can Bill Project'].enabled),
            canHoldProject: Boolean(latestWorkflowPermissions['Can Hold Project'].enabled),
            canCancelProject: Boolean(latestWorkflowPermissions['Can Cancel Project'].enabled),
            canReceivePayment: Boolean(latestWorkflowPermissions['Can Receive Payment'].enabled),
            canReopenProject: Boolean(latestWorkflowPermissions['Can Reopen Project'].enabled),
            
            // Project-specific permissions
            canChangeProjectName: Boolean(projectSpecificPermissions['Can Change Project Name'].enabled),
            canChangeArticleNumber: Boolean(projectSpecificPermissions['Can Change Article Number'].enabled),
            canChangeCompanyName: Boolean(projectSpecificPermissions['Can Change Company Name'].enabled),
            canTransferProject: Boolean(projectSpecificPermissions['Can Transfer Project'].enabled),
            canChangeProjectDescription: Boolean(projectSpecificPermissions['Can Change Project Description'].enabled),
            canChangeProjectRemarks: Boolean(projectSpecificPermissions['Can Change Project Remarks'].enabled),
            canChangeProjectCost: Boolean(projectSpecificPermissions['Can Change Project Cost'].enabled),
            canUploadProductImage: Boolean(projectSpecificPermissions['Can Upload Product Image'].enabled),
            
            canAddProject: false, // Adding missing property that's required by PermissionData
          },
        };

        console.log('Designation data being sent:', JSON.stringify(designationData, null, 2));

        // Close modal immediately for better UX
        setShowModal(false);
        document.body.style.overflow = 'auto';

        // Reset form state immediately
        setDesignationTitle('');
        setEditingDesignation(null);
        setCurrentStep(1);
        resetFormState();

        if (editingDesignation) {
          // KEEP THIS TOAST - Use withToast for updating designations
          withToast(
            () =>
              dispatch(
                updatePermission({
                  id: editingDesignation.id,
                  data: designationData,
                })
              ).unwrap(),
            {
              loading: 'Updating designation...',
              success: 'Designation updated successfully',
              error: error =>
                `Update failed: ${typeof error === 'string' ? error : error?.message || 'Unknown error'}`,
            }
          )
            .then(() => {
              // Refresh designations list without toast
              return dispatch(
                fetchPermissions({ page: 1, limit: visibleDesignations.length })
              ).unwrap();
            })
            .then(response => {
              if (response?.data) {
                setVisibleDesignations(response.data);
              }
              dispatch(clearPermissionError());
            })
            .finally(() => {
              setLocalLoading(false);
              setSaveLoading(false);
              dispatch(clearCurrentPermission());
            });
        } else {
          // KEEP THIS TOAST - Use withToast for creating designations
          withToast(() => dispatch(createPermission(designationData)).unwrap(), {
            loading: 'Creating new designation...',
            success: 'Designation created successfully',
            error: error =>
              `Creation failed: ${typeof error === 'string' ? error : error?.message || 'Unknown error'}`,
          })
            .then(() => {
              // Refresh the list with all current items plus one more page without toast
              return dispatch(
                fetchPermissions({ page: 1, limit: visibleDesignations.length + 10 })
              ).unwrap();
            })
            .then(response => {
              if (response?.data) {
                setVisibleDesignations(response.data);
              }
              dispatch(clearPermissionError());
            })
            .finally(() => {
              setLocalLoading(false);
              setSaveLoading(false);
              dispatch(clearCurrentPermission());
            });
        }
      } catch (error) {
        console.error('Failed to process designation:', error);
        // Keep this critical error toast
        toast.error('An unexpected error occurred');
        dispatch(clearPermissionError());
        setSaveLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setDesignationTitle('');
    setEditingDesignation(null);
    setCurrentStep(1);
    resetFormState();
    setLocalLoading(false);
    setSaveLoading(false);
    setViewOnlyMode(false);
    dispatch(clearCurrentPermission());
  };

  const handleViewDesignation = async (designation: DesignationItem) => {
    // View is allowed if user has read permission
    if (!userPermissions.roleRead && user?.designation !== 'superAdmin') {
      toast.error("You don't have permission to view designations", {
        duration: 3000,
      });
      return;
    }

    // Set local loading state
    setLocalLoading(true);

    // Set view-only mode
    setViewOnlyMode(true);
    setEditingDesignation(designation);
    setCurrentStep(2);
    setShowModal(true);

    try {
      // Fetch the designation data without toast
      await dispatch(fetchPermissionById(designation.id)).unwrap();
      // The useEffect will populate the form once data is loaded
    } catch (error) {
      console.error('Error fetching designation details:', error);
      setLocalLoading(false);
    }
  };

  const handlePermissionToggle = (entity: string, permission: keyof EntityPermission) => {
    console.log(
      `Toggling ${entity}.${permission} from`,
      entityPermissions[entity][permission],
      'to',
      !entityPermissions[entity][permission]
    );

    // Check if we're turning a permission off
    const isDisabling = entityPermissions[entity][permission];

    // Special debug for Role permissions
    if (entity === 'Role') {
      console.log(
        `[DEBUG] Role permission ${permission} toggled to: ${!entityPermissions[entity][permission]}`
      );
    }

    // Use functional update to ensure we're working with the latest state
    setEntityPermissions(prevState => {
      const newState = { ...prevState };

      // If toggling a read permission to false, also disable write, edit, and manage (for Employee)
      // Exception: For Project entity, edit permission is independent of read permission
      if (permission === 'read' && prevState[entity][permission] === true) {
        if (entity === 'Project') {
          // For Project, only disable create (write) when read is disabled, but keep edit independent
          newState[entity] = {
            ...prevState[entity],
            read: false,
            create: false,
            // edit remains unchanged for Project entity
          };
        } else {
          newState[entity] = {
            ...prevState[entity],
            read: false,
            create: false,
            edit: false,
            ...(entity === 'Employee' && { manage: false }), // Only add manage for Employee
          };
        }
      }
      // If trying to enable write, edit, or manage when read is disabled, don't allow it
      // Exception: For Project entity, edit permission is independent of read permission
      else if ((permission === 'create' || permission === 'edit' || permission === 'manage') && !prevState[entity].read) {
        if (entity === 'Project' && permission === 'edit') {
          // Allow Project edit to be enabled even when read is disabled
          newState[entity] = {
            ...prevState[entity],
            [permission]: !prevState[entity][permission],
          };
        } else {
          // Read is required for write, edit, and manage for other entities, so don't change anything if read is off
          return prevState;
        }
      }
      // Normal toggle behavior for all other cases
      else {
        newState[entity] = {
          ...prevState[entity],
          [permission]: !prevState[entity][permission], // Toggle the value based on current state
        };
      }

      // Debug log updated state for Role permissions
      if (entity === 'Role') {
        console.log('[DEBUG] Updated Role permissions:', newState.Role);
      }

      return newState;
    });

    // If we're disabling a permission, immediately turn off the "Enable All" toggle
    if (isDisabling) {
      setEnableAllPermissions(false);
    }
    // If we're enabling a permission, check if this would make all permissions enabled
    else {
      // We need to check all permissions in the next tick when state has been updated
      setTimeout(() => {
        setEnableAllPermissions(checkIfAllPermissionsEnabled());
      }, 0);
    }
  };

  const handleWorkflowToggle = (workflow: string) => {
    console.log(
      `Toggling workflow ${workflow} from`,
      workflowPermissions[workflow].enabled,
      'to',
      !workflowPermissions[workflow].enabled
    );

    // Check if we're turning a permission off
    const isDisabling = workflowPermissions[workflow].enabled;

    // Use functional update to ensure we're working with the latest state
    setWorkflowPermissions(prevState => {
      const newState = {
        ...prevState,
        [workflow]: {
          enabled: !prevState[workflow].enabled, // Toggle based on current state
        },
      };

      console.log(`[DEBUG] Updated ${workflow} permission:`, newState[workflow]);

      return newState;
    });

    // If we're disabling a permission, immediately turn off the "Enable All" toggle
    if (isDisabling) {
      setEnableAllPermissions(false);
    }
    // If we're enabling a permission, check if this would make all permissions enabled
    else {
      // We need to check all permissions in the next tick when state has been updated
      setTimeout(() => {
        setEnableAllPermissions(checkIfAllPermissionsEnabled());
      }, 0);
    }
  };

  // Function to toggle all permissions at once
  const handleToggleAllPermissions = () => {
    const newValue = !enableAllPermissions;
    setEnableAllPermissions(newValue);

    // Update entity permissions
    setEntityPermissions(prevState => {
      const newState = { ...prevState };
      Object.keys(newState).forEach(entity => {
        newState[entity] = {
          read: newValue,
          create: newValue,
          edit: newValue,
          ...(entity === 'Employee' && { manage: newValue }), // Only add manage for Employee
        };
      });
      return newState;
    });

    // Update Finance Access and Application Access permissions
    setDashboardPermission(prevState => ({
      ...prevState,
      'Finance Access': { enabled: newValue },
      'Application Access': { enabled: newValue },
    }));

    // Update workflow permissions
    setWorkflowPermissions(prevState => {
      const newState = { ...prevState };
      Object.keys(newState).forEach(workflow => {
        newState[workflow] = {
          enabled: newValue,
        };
      });
      return newState;
    });

    // Update project-specific permissions
    setProjectSpecificPermissions(prevState => {
      const newState = { ...prevState };
      Object.keys(newState).forEach(permission => {
        newState[permission] = {
          enabled: newValue,
        };
      });
      return newState;
    });
  };

  // Function to check if all permissions are enabled
  const checkIfAllPermissionsEnabled = () => {
    // Check entity permissions
    for (const entity in entityPermissions) {
      for (const permission in entityPermissions[entity]) {
        if (!entityPermissions[entity][permission as keyof EntityPermission]) {
          return false;
        }
      }
    }

    // Check workflow permissions
    for (const key in workflowPermissions) {
      if (!workflowPermissions[key].enabled) {
        return false;
      }
    }

    // Check finance access and application access permissions
    if (!dashboardPermission['Finance Access'].enabled || !dashboardPermission['Application Access'].enabled) {
      return false;
    }

    // Check project-specific permissions
    for (const key in projectSpecificPermissions) {
      if (!projectSpecificPermissions[key].enabled) {
        return false;
      }
    }

    // Check project-specific permissions
    for (const key in projectSpecificPermissions) {
      if (!projectSpecificPermissions[key].enabled) {
        return false;
      }
    }

    return true;
  };

  // Fixed the closing tag issue in the renderPermissionTable function
  const renderPermissionTable = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">
              Entity Permissions
            </th>
            <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
              Read
            </th>
            <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
              Create
            </th>
            <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
              Edit
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {Object.entries(entityPermissions).map(([entity, permissions]) => (
            <>
              <tr key={entity} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <div className="flex items-center gap-3">
                    {entityIcons[entity]}
                    <span>{entity}</span>
                  </div>
                </td>
                <td key={`${entity}-read`} className="px-2 py-4 whitespace-nowrap text-center">                    <Toggle
                      enabled={permissions.read}
                      onChange={() => !viewOnlyMode && handlePermissionToggle(entity, 'read')}
                      size="sm"
                      disabled={viewOnlyMode}
                    />
                </td>
                <td key={`${entity}-create`} className="px-2 py-4 whitespace-nowrap text-center">
                  <div className={!permissions.read ? 'opacity-50' : ''}>
                    <Toggle
                      enabled={permissions.create}
                      onChange={() => !viewOnlyMode && handlePermissionToggle(entity, 'create')}
                      size="sm"
                      disabled={viewOnlyMode || !permissions.read}
                    />
                  </div>
                </td>
                <td key={`${entity}-edit`} className="px-2 py-4 whitespace-nowrap text-center">
                  <div className={!permissions.read && entity !== 'Project' ? 'opacity-50' : ''}>
                    <Toggle
                      enabled={permissions.edit}
                      onChange={() => !viewOnlyMode && handlePermissionToggle(entity, 'edit')}
                      size="sm"
                      disabled={viewOnlyMode || (!permissions.read && entity !== 'Project')}
                    />
                  </div>
                </td>
              </tr>
              {/* Expanded row for Employee manage permission */}
              {entity === 'Employee' && permissions.create && (
                <tr key={`${entity}-manage-row`} className="bg-blue-50 border-l-4 border-blue-400">
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                    <div className="flex items-center gap-3 ml-6">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      </div>
                      <span className="font-medium">Manage Employee</span>
                      <span className="text-xs text-gray-500">(Advanced permission)</span>
                    </div>
                  </td>
                  <td className="px-2 py-3 whitespace-nowrap text-center">
                    <span className="text-gray-400">—</span>
                  </td>
                  <td className="px-2 py-3 whitespace-nowrap text-center">
                    <div className={!permissions.read ? 'opacity-50' : ''}>
                      <Toggle
                        enabled={permissions.manage || false}
                        onChange={() => !viewOnlyMode && handlePermissionToggle(entity, 'manage')}
                        size="sm"
                        disabled={viewOnlyMode || !permissions.read}
                      />
                    </div>
                  </td>
                  <td className="px-2 py-3 whitespace-nowrap text-center">
                    <span className="text-gray-400">—</span>
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Function to handle toggling project-specific permissions
  const handleProjectSpecificToggle = (permission: string) => {
    console.log(
      `Toggling project-specific permission ${permission} from`,
      projectSpecificPermissions[permission].enabled,
      'to',
      !projectSpecificPermissions[permission].enabled
    );

    // Check if we're turning a permission off
    const isDisabling = projectSpecificPermissions[permission].enabled;

    // Use functional update to ensure we're working with the latest state
    setProjectSpecificPermissions(prevState => {
      const newState = {
        ...prevState,
        [permission]: {
          enabled: !prevState[permission].enabled, // Toggle based on current state
        },
      };

      console.log(`[DEBUG] Updated ${permission} permission:`, newState[permission]);

      return newState;
    });

    // If we're disabling a permission, immediately turn off the "Enable All" toggle
    if (isDisabling) {
      setEnableAllPermissions(false);
    }
    // If we're enabling a permission, check if this would make all permissions enabled
    else {
      // We need to check all permissions in the next tick when state has been updated
      setTimeout(() => {
        setEnableAllPermissions(checkIfAllPermissionsEnabled());
      }, 0);
    }
  };

  const renderWorkflowTable = () => {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden mt-6">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Workflow Permissions
              </th>
              <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                Access
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Object.entries(workflowPermissions).map(([workflow, { enabled }]) => (
              <tr key={workflow} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <div className="flex items-center gap-3">
                    {workflowIcons[workflow]}
                    <span>{workflow}</span>
                  </div>
                </td>
                <td className="px-2 py-4 whitespace-nowrap text-center">                <Toggle
                  enabled={enabled}
                  onChange={() => !viewOnlyMode && handleWorkflowToggle(workflow)}
                  size="sm"
                  disabled={viewOnlyMode}
                />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderDashboardAccessTable = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden mt-6">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Dashboard Access
            </th>
            <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
              Access
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {Object.entries(dashboardPermission).map(([permission, { enabled }]) => (
            <tr key={permission} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                <div className="flex items-center gap-3">
                  {dashboardIcons[permission]}
                  <span>{permission}</span>
                </div>
              </td>
              <td className="px-2 py-4 whitespace-nowrap text-center">
                <Toggle
                  enabled={enabled}
                  onChange={() => !viewOnlyMode && handleDashboardToggle(permission)}
                  size="sm"
                  disabled={viewOnlyMode}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderProjectSpecificTable = () => {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden mt-6">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Project-Specific Permissions
              </th>
              <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                Access
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Object.entries(projectSpecificPermissions).map(([permission, { enabled }]) => (
              <tr key={permission} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <div className="flex items-center gap-3">
                    {projectSpecificIcons[permission.replace('Can ', '')]}
                    <span>{permission}</span>
                  </div>
                </td>
                <td className="px-2 py-4 whitespace-nowrap text-center">
                  <Toggle
                    enabled={enabled}
                    onChange={() => !viewOnlyMode && handleProjectSpecificToggle(permission)}
                    size="sm"
                    disabled={viewOnlyMode}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Handle search input change with debounce
  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.trim().length === 0) {
      dispatch(clearSearchResults());
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    // Set new timeout for debouncing
    searchTimeoutRef.current = setTimeout(() => {
      dispatch(searchPermissions(query.trim()))
        .unwrap()
        .finally(() => {
          setIsSearching(false);
        });
    }, 300);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    dispatch(clearSearchResults());
    setIsSearching(false);
  };

  // Highlight matching text in search results
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim() || !text) return text;

    const regex = new RegExp(`(${query.trim()})`, 'gi');
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part: string, i: number) =>
          regex.test(part) ? (
            <span key={i} className="bg-yellow-200 font-medium">
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  };

  // If user has no permission at all, show access denied message
  if (!userPermissions.roleRead && user?.designation !== 'superAdmin') {
    return (
      <ComponentWrapper
        icon={<MdLockOutline size={24} />}
        title="Access Denied"
        className="min-h-[70vh] flex items-center justify-center"
      >
        <div className="text-center py-12 max-w-lg mx-auto">
          <div className="bg-red-100 text-red-600 p-4 rounded-full inline-flex mb-6">
            <MdLockOutline size={48} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Permission Required</h2>
          <p className="text-gray-600 mb-6">
            You don't have permission to access the Designations & Access Control page. Please
            contact your administrator if you need access to this feature.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </ComponentWrapper>
    );
  }

  return (
    <>
      <ComponentWrapper
        icon={<IoKeyOutline size={24} />}
        title="Designations & Access Control"
        buttonOne={
          // Only show create button if user has write permission
          userPermissions.roleWrite || user?.designation === 'superAdmin' ? (
            <ActionButton
              onClick={handleCreateDesignation}
              icon={<CiLock size={24} />}
              variant="primary"
              className="hidden md:flex"
              disabled={false}
            >
              Create New Designation
            </ActionButton>
          ) : null
        }
        className={`min-h-[70vh] pb-20 md:pb-10`}
      >
        {/* Search Bar */}
        <div className="mt-6 mb-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {isSearching ? (
                <AiOutlineLoading3Quarters className="animate-spin h-5 w-5 text-gray-400" />
              ) : (
                <FiSearch className="h-5 w-5 text-gray-400" />
              )}
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
              placeholder="Search designations..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <button
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={handleClearSearch}
              >
                <MdClose className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>

        {/* Recent Designations Header */}
        <div className="mt-2 bg-gray-200 mb-6 rounded-xl py-3 px-4 flex items-center gap-2">
          <RiFileListLine className="text-gray-500" />
          <h3 className="text-gray-500 font-medium">
            {searchQuery ? 'SEARCH RESULTS' : 'RECENT DESIGNATIONS'}
          </h3>
        </div>

        {/* Designations List */}
        <div className="mt-2">
          {/* Show search results if there's a search query */}
          {searchQuery ? (
            searching ? (
              <div className="flex justify-center items-center py-12">
                <LoadingAnimation />
              </div>
            ) : searchResults.length > 0 ? (
              <div className="grid gap-4">
                {searchResults.map((designation, index) => (
                  <div
                    key={designation.id}
                    className="border-2 border-gray-200 rounded-lg p-4 flex justify-between items-center bg-white hover:border-blue-500 transition-colors shadow-md cursor-pointer"
                    onClick={() => handleViewDesignation(designation as unknown as DesignationItem)}
                  >
                    <div className="flex gap-3 items-center">
                      <span className="w-8 h-8 flex items-center justify-center bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium shadow-lg">
                        {index + 1}
                      </span>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {highlightMatch(designation.name, searchQuery)}
                        </h3>
                        <p className="text-gray-500 text-sm mt-1">
                          Created on:{' '}
                          {designation.createdAt
                            ? new Date(designation.createdAt).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                    {/* Only show edit button if user has edit permission */}
                    {(userPermissions.roleEdit || user?.designation === 'superAdmin') && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleEditDesignation(designation as unknown as DesignationItem);
                        }}
                        className="px-3 py-1 border border-blue-800 text-blue-800 rounded-lg hover:bg-blue-50 flex items-center gap-1"
                        disabled={loading || localLoading}
                      >
                        <BiEdit /> Edit
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  No designations found matching "{searchQuery}".
                </p>
                <p className="text-gray-400 text-sm mt-2">
                  Try using different keywords or partial terms.
                </p>
              </div>
            )
          ) : !visibleDesignations.length && loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingAnimation />
            </div>
          ) : visibleDesignations.length > 0 ? (
            <div className="grid gap-4">
              {visibleDesignations.map((designation, index) => (
                <div
                  key={designation.id}
                  className="border-2 border-gray-200 rounded-lg p-4 flex justify-between items-center bg-white hover:border-blue-500 transition-colors shadow-md cursor-pointer"
                  onClick={() => handleViewDesignation(designation)}
                >
                  <div className="flex gap-3 items-center">
                    <span className="w-8 h-8 flex items-center justify-center bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium shadow-lg">
                      {index + 1}
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{designation.name}</h3>
                      <p className="text-gray-500 text-sm mt-1">
                        Created on:{' '}
                        {designation.createdAt
                          ? new Date(designation.createdAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                  {/* Only show edit button if user has edit permission */}
                  {(userPermissions.roleEdit || user?.designation === 'superAdmin') && (
                    <button
                      onClick={e => {
                        e.stopPropagation(); // Prevent triggering the parent div onClick
                        handleEditDesignation(designation);
                      }}
                      className="px-3 py-1 border border-blue-800 text-blue-800 rounded-lg hover:bg-blue-50 flex items-center gap-1"
                      disabled={loading || localLoading}
                    >
                      <BiEdit /> Edit
                    </button>
                  )}
                </div>
              ))}

              {/* Modern loading indicator for infinite scroll */}
              {isFetchingMore && (
                <div className="flex justify-center items-center py-6 mt-2">
                  <div className="relative">
                    <div className="w-12 h-12">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div
                          className="w-6 h-6 border-4 border-red-500 border-b-transparent rounded-full animate-spin"
                          style={{ animationDuration: '1.5s' }}
                        ></div>
                      </div>
                    </div>
                    <div className="mt-4 text-center">
                      <span className="bg-gradient-to-r from-blue-600 to-red-500 bg-clip-text text-transparent font-medium">
                        Loading more designations...
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                No designations found.{' '}
                {userPermissions.roleWrite || user?.designation === 'superAdmin'
                  ? 'Create your first designation to get started.'
                  : 'Please contact your administrator to add designations.'}
              </p>
            </div>
          )}
        </div>
      </ComponentWrapper>

      {/* Designation Modal */}
      <PopupWrapper
        logo={<MdOutlineSecurity size={53} />}
        title={
          editingDesignation ? (
            <div className="flex flex-col gap-1">
              <span className="text-4xl">{designationTitle}</span>
              <span className="text-sm text-gray-500">
                {viewOnlyMode
                  ? 'View permissions for this designation'
                  : 'Modify permissions for this designation'}
              </span>
            </div>
          ) : (
            'Create Designation'
          )
        }
        isOpen={showModal}
        onClose={handleCloseModal}
        headerComponent={
          !editingDesignation ? (
            <div className="flex items-center justify-center gap-2 p-3 border-2 border-gray-200 bg-gray-50 rounded-lg">
              <SiEndeavouros className="text-blue-600" size={20} />
              <span className="font-medium text-gray-700">Step {currentStep} of 2</span>
            </div>
          ) : null
        }
        size="lg"
      >
        <div className="relative">
          {localLoading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingAnimation />
            </div>
          ) : currentStep === 1 && !editingDesignation ? (
            <div className="space-y-6">
              <InputField
                label="Designation Name"
                placeholder="Enter Designation Name"
                value={designationTitle}
                onChange={e => setDesignationTitle(e.target.value)}
              />
              <button
                onClick={handleSaveDesignation}
                className="w-full py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all duration-200 font-semibold shadow-sm flex items-center justify-center gap-2 active:scale-95 disabled:opacity-70 disabled:hover:bg-red-500 disabled:cursor-not-allowed disabled:active:scale-100"
                disabled={saveLoading || !designationTitle.trim()}
              >
                {saveLoading ? (
                  <>
                    <AiOutlineLoading3Quarters className="animate-spin" size={20} /> Processing...
                  </>
                ) : (
                  <>
                    Next <BiSend size={20} />
                  </>
                )}
              </button>
              <p className="text-sm text-gray-500 mt-1">
                All permissions should be connected with the relevant designations only.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {!editingDesignation && (
                <h3 className="text-lg font-medium text-gray-900 mb-4">Access Control</h3>
              )}

              {/* Enable All Permissions Toggle */}
              {!viewOnlyMode && (
                <div className="bg-blue-50 rounded-lg p-4 pr-10 mb-6 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <MdOutlineSecurity className="text-blue-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-medium text-blue-900">Enable All Permissions</h3>
                      <p className="text-xs text-blue-700">
                        Toggle to quickly enable or disable all permissions at once
                      </p>
                    </div>
                  </div>
                  <Toggle
                    enabled={enableAllPermissions}
                    onChange={handleToggleAllPermissions}
                    size="sm"
                    disabled={viewOnlyMode}
                  />
                </div>
              )}

              {renderPermissionTable()}
              {renderWorkflowTable()}
              {renderDashboardAccessTable()}
              {renderProjectSpecificTable()}

              {/* Only show save button if we're not in view-only mode and user has proper permissions */}
              {!viewOnlyMode &&
                (userPermissions.roleWrite ||
                  (editingDesignation && userPermissions.roleEdit) ||
                  user?.designation === 'superAdmin') && (
                  <button
                    onClick={handleSaveDesignation}
                    className="w-full py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all duration-200 font-semibold shadow-sm flex items-center justify-center gap-2 active:scale-95 disabled:opacity-70 disabled:hover:bg-red-500 disabled:cursor-not-allowed disabled:active:scale-100"
                    disabled={saveLoading}
                  >
                    {saveLoading ? (
                      <>
                        <AiOutlineLoading3Quarters className="animate-spin" size={20} />
                        {editingDesignation ? 'Updating...' : 'Saving...'}
                      </>
                    ) : (
                      <>
                        {editingDesignation ? 'Update Designation' : 'Save'} <BiSend size={20} />
                      </>
                    )}
                  </button>
                )}

              {viewOnlyMode && (
                <div className="text-center text-gray-500 mt-2">
                  <p>This is a read-only view of the designation.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </PopupWrapper>

      {/* Mobile Action Button - only show if user has permission */}
      {(userPermissions.roleWrite || user?.designation === 'superAdmin') && (
        <ActionButton
          onClick={handleCreateDesignation}
          icon={<CiLock size={20} />}
          variant="primary"
          fullWidth={true}
          mobileFixed={true}
          className="md:hidden"
          disabled={loading || localLoading}
        >
          Create New Designation
        </ActionButton>
      )}
    </>
  );
};

export default DesignationPage;
